/**
 * MoveNodeTooltip Component
 *
 * Tooltip that appears when clicking a move node on the series canvas.
 *
 * Features:
 * - Displays move name, description, and embedded video
 * - Navigate button to move detail page
 * - Delete button with confirmation modal
 * - Click outside to close
 */

import { X, ExternalLink, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmationModal } from '@/components/shared/ConfirmationModal'
import { useState } from 'react'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

interface MoveNodeTooltipProps {
  move: {
    id: string
    name: string
    description: string
    thumbnailUrl?: string | null
    url?: string | null
    taggings?: Array<{
      id: string
      tagId: string
      tag: {
        id: string
        name: string
        description?: string | null
        color?: string | null
      }
      deletedAt?: string | null
    }>
  }
  position: { x: number; y: number }
  onClose: () => void
  onNavigate: () => void
  onDelete: () => void
}

export function MoveNodeTooltip({
  move,
  position,
  onClose,
  onNavigate,
  onDelete,
}: MoveNodeTooltipProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const sanitizedDescription = sanitizeHtml(move.description)

  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    onDelete()
    setShowDeleteConfirm(false)
    onClose()
  }

  // Extract video ID from YouTube URL
  const getYouTubeEmbedUrl = (url: string) => {
    const videoIdMatch = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/
    )
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`
    }
    return null
  }

  const embedUrl = move.url ? getYouTubeEmbedUrl(move.url) : null

  const isBrowser = typeof window !== 'undefined'
  const isMobile = isBrowser && window.innerWidth < 768

  const tooltipStyle = isBrowser
    ? isMobile
      ? {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }
      : {
          left: `${Math.min(position.x, window.innerWidth - 416)}px`,
          top: `${Math.min(position.y, window.innerHeight - 500)}px`,
        }
    : undefined

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="tooltip-backdrop"
        className="fixed inset-0 z-40 bg-black/20"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Tooltip */}
      <div
        className="fixed z-50 w-[400px] max-w-[calc(100vw-2rem)] rounded-lg border bg-white shadow-2xl"
        style={tooltipStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tooltip-title"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b p-4">
          <h3
            id="tooltip-title"
            className="flex-1 pr-2 text-lg font-semibold text-black"
          >
            {move.name}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="flex-shrink-0 text-[#666666] hover:text-black"
            aria-label="Close tooltip"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {/* Video Embed / Thumbnail */}
          {embedUrl ? (
            <div className="mb-4 aspect-video w-full overflow-hidden rounded-lg bg-black">
              <iframe
                src={embedUrl}
                title={move.name}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : move.thumbnailUrl ? (
            <div className="mb-4 aspect-video w-full overflow-hidden rounded-lg bg-black/5">
              <a
                href={move.url || undefined}
                target={move.url ? '_blank' : undefined}
                rel={move.url ? 'noopener noreferrer' : undefined}
              >
                <img
                  src={move.thumbnailUrl}
                  alt={move.name}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </a>
            </div>
          ) : move.url ? (
            <div className="mb-4 rounded-lg border border-[#e5e7eb] bg-[#F9F9F9] p-4">
              <p className="mb-2 text-sm text-[#666666]">Video URL:</p>
              <a
                href={move.url}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-sm text-[#0033A0] hover:underline"
              >
                {move.url}
              </a>
            </div>
          ) : (
            <div className="mb-4 flex aspect-video w-full items-center justify-center rounded-lg bg-gradient-to-br from-[#0033A0]/10 to-[#6B3FA0]/10">
              <p className="text-sm text-[#666666]">No video available</p>
            </div>
          )}

          {/* Description */}
          {sanitizedDescription && (
            <div className="mb-4">
              <h4 className="mb-2 text-sm font-semibold text-black">
                Description
              </h4>
              <div
                className="text-sm leading-relaxed text-[#666666]"
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
              />
            </div>
          )}

          {/* Tags */}
          {move.taggings && move.taggings.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-2 text-sm font-semibold text-black">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {move.taggings.map(tagging => (
                  <span
                    key={tagging.id}
                    className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
                    style={{
                      backgroundColor: tagging.tag.color
                        ? `${tagging.tag.color}20`
                        : '#F3F4F6',
                      color: tagging.tag.color || '#6B7280',
                      border: `1px solid ${tagging.tag.color || '#E5E7EB'}`,
                    }}
                  >
                    {tagging.tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between border-t p-4">
          <Button
            variant="outline"
            onClick={onNavigate}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            View Details
          </Button>
          <Button
            variant="outline"
            onClick={handleDelete}
            className="flex items-center gap-2 border-[#D62828] text-[#D62828] hover:bg-red-50 hover:text-[#D62828]"
          >
            <Trash2 className="h-4 w-4" />
            Remove
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Remove Move from Series?"
        description={`Remove "${move.name}" from this series? This will also delete all connections involving this move. The move itself will not be deleted.`}
        confirmText="Remove"
        confirmVariant="destructive"
      />
    </>
  )
}
